import { EMPTY, merge, Observable, race, Subject, timer } from 'rxjs';
import { map, takeUntil, takeWhile, tap } from 'rxjs/operators';
import {
  TimeoutConfig,
  DEFAULT_TIMEOUT_CONFIG,
  PERSISTENT_TIMEOUT_CONFIG,
} from '../../interfaces/host-message-types';

/** Maximum safe delay for `setTimeout` / RxJS `timer` (2^31 − 1 ms ≈ 24.8 days). */
const MAX_TIMER_MS = 2_147_483_647;

/** Status of a timeout operation */
export type TimeoutStatus = 'pending' | 'completed' | 'timeout' | 'cancelled';

/** Result of a timeout operation */
export interface TimeoutResult<T> {
  status: TimeoutStatus;
  data?: T;
  error?: string;
  attempts: number;
}

/** Callback type for retry actions */
export type RetryCallback = (attempt: number) => void;

/**
 * Manages timeout and retry logic for host communication operations.
 *
 * Internally composes RxJS `timer` + `race` so that only the first
 * resolution (complete / cancel / timeout / retries-exhausted) wins.
 * No manual `setInterval`/`setTimeout` – cleanup is fully automatic.
 */
export class HostTimeoutHandler<T = unknown> {
  private readonly cancel$ = new Subject<void>();
  private readonly complete$ = new Subject<T>();
  private status: TimeoutStatus = 'pending';
  private attempts = 0;

  constructor(private readonly config: TimeoutConfig = DEFAULT_TIMEOUT_CONFIG) {}

  /**
   * Starts the timeout handler with periodic retry.
   *
   * @param onRetry Called on each retry attempt (including the first one)
   * @returns Observable that emits exactly one `TimeoutResult`, then completes
   */
  start(onRetry?: RetryCallback): Observable<TimeoutResult<T>> {
    this.status = 'pending';
    this.attempts = 0;

    const done$ = merge(this.cancel$, this.complete$);
    const totalTicks = this.config.maxRetries + 1;

    // --- Side-effect stream: fires onRetry on each tick, no terminal emission ---
    const retry$ = timer(0, this.config.retryIntervalMs).pipe(
      takeUntil(done$),
      takeWhile(tick => tick < totalTicks),
      tap(tick => {
        this.attempts = tick + 1;
        onRetry?.(this.attempts);
      })
    );

    // --- Terminal streams (each emits exactly one result) ---

    // Retries exhausted: only relevant when the computed delay is within safe timer range.
    // For "unlimited" configs the delay overflows MAX_TIMER_MS so we skip it entirely
    // and let the overall timeout (or complete/cancel) resolve the race instead.
    const retriesDelayMs = totalTicks * this.config.retryIntervalMs;
    const retriesExhausted$: Observable<TimeoutResult<T>> =
      retriesDelayMs > MAX_TIMER_MS
        ? EMPTY
        : timer(retriesDelayMs).pipe(
            takeUntil(done$),
            map(() => ({
              status: 'timeout' as const,
              attempts: this.attempts,
              error: `Max retries (${this.config.maxRetries}) exceeded`,
            }))
          );

    const overallTimeout$: Observable<TimeoutResult<T>> = timer(this.config.timeoutMs).pipe(
      takeUntil(done$),
      map(() => ({
        status: 'timeout' as const,
        attempts: this.attempts,
        error: `Operation timed out after ${this.config.timeoutMs}ms`,
      }))
    );

    const completed$: Observable<TimeoutResult<T>> = this.complete$.pipe(
      map(data => ({
        status: 'completed' as const,
        data,
        attempts: this.attempts,
      }))
    );

    const cancelled$: Observable<TimeoutResult<T>> = this.cancel$.pipe(
      map(() => ({
        status: 'cancelled' as const,
        attempts: this.attempts,
      }))
    );

    // `race` subscribes to all, takes the first emission, then auto-unsubscribes the rest
    return new Observable<TimeoutResult<T>>(subscriber => {
      const retrySub = retry$.subscribe();

      const resultSub = race(completed$, cancelled$, overallTimeout$, retriesExhausted$).subscribe(
        result => {
          this.status = result.status;
          subscriber.next(result);
          subscriber.complete();
        }
      );

      return () => {
        retrySub.unsubscribe();
        resultSub.unsubscribe();
      };
    });
  }

  /** Signals successful completion with optional data. */
  complete(data?: T): void {
    if (this.status === 'pending') {
      this.complete$.next(data as T);
    }
  }

  /** Cancels the timeout operation. */
  cancel(): void {
    if (this.status === 'pending') {
      this.cancel$.next();
    }
  }

  /** Current status of the handler. */
  getStatus(): TimeoutStatus {
    return this.status;
  }

  /** Number of retry attempts made so far. */
  getAttempts(): number {
    return this.attempts;
  }

  /** Whether the handler is still waiting for resolution. */
  isPending(): boolean {
    return this.status === 'pending';
  }
}

/**
 * Factory: creates a persistent handler that retries every 10 s
 * until externally completed or cancelled (~24.8 days max).
 */
export function createPersistentAwaitingTokenHandler(): HostTimeoutHandler<string> {
  return new HostTimeoutHandler<string>(PERSISTENT_TIMEOUT_CONFIG);
}
